import type { CategoryRow } from '../catalog/categories';
import type { BomCostChangeReply } from '../deals/bomCostChange';
import type { PrintSheetLayout, PrintSheetHtml } from '../print/templates';
import type { PrintDocument } from './printRegions';
import { transitiveChain } from '../records/transitive';
import { addBusinessDays } from '../reminders/schedule';
import { roundHalfUpCents } from '../money';
import { counted } from '../composites/countedUnit';

/**
 * Vendor reps and the request for quotation (piece 22; decisions 0121, 0152,
 * 0158): the three-list union that suggests vendors, the unassigned-lines
 * block, the chase and the no-response alarm as pure rules, and the comparison
 * matrix the screen draws. The service reads rows and sends mail; nothing here
 * touches a table.
 */
export type VendorSuggestionSource = 'lineVendor' | 'itemSupplier' | 'vendorRep' | 'priorPurchaseOrder';

export interface UnionLine {
  lineId: string;
  itemId: string;
  categoryId: string | null;
  /** The line's own vendor where a person named one. */
  vendorId?: string | null;
}

export interface SupplierLink {
  itemId: string;
  companyId: string;
}

export interface RepLink {
  vendorCompanyId: string;
  itemCategoryId: string;
  isActive: boolean;
}

export interface PriorPurchase {
  itemId: string;
  vendorCompanyId: string;
}

export interface UnionFacts {
  /** The active, offered ItemSupplier rows; the caller has already dropped Blacklisted ones (decision 0337). */
  suppliers: readonly SupplierLink[];
  reps: readonly RepLink[];
  /** The category tree, as much of a row as the parent walk reads. */
  categories: readonly Pick<CategoryRow, 'id' | 'parentId'>[];
  /** The vendors on prior Purchase Orders for the item, imported history included. */
  priorPurchases: readonly PriorPurchase[];
}

export interface VendorSuggestion {
  lineIds: string[];
  /** Which list suggested each line, so the picker can say why. */
  sources: Record<string, VendorSuggestionSource[]>;
}

/**
 * For each vendor, the lines whose item has an active ItemSupplier row naming
 * them, plus lines whose item's category or any ancestor of it they rep, plus
 * lines whose item they supplied on a prior Purchase Order. 💰 ItemSupplier
 * alone drops a projected line, which is the case the RFQ exists for; the rep
 * list is what carries it.
 */
export function vendorLineUnion(lines: readonly UnionLine[], facts: UnionFacts): Map<string, VendorSuggestion> {
  const union = new Map<string, VendorSuggestion>();
  const add = (vendorId: string, lineId: string, source: VendorSuggestionSource) => {
    const entry = union.get(vendorId) ?? { lineIds: [], sources: {} };
    if (!entry.lineIds.includes(lineId)) entry.lineIds.push(lineId);
    const sources = entry.sources[lineId] ?? [];
    if (!sources.includes(source)) sources.push(source);
    entry.sources[lineId] = sources;
    union.set(vendorId, entry);
  };
  const activeReps = facts.reps.filter((r) => r.isActive);
  // The chain above a category, nearest first, with the cycle guard the whole catalog shares (the walk ancestorsOf makes).
  const parentOf = new Map(facts.categories.map((c) => [c.id, c.parentId]));
  const ancestors = (id: string) => transitiveChain(id, (c) => parentOf.get(c) ?? null, { label: 'ItemCategory.parentId' });
  for (const line of lines) {
    if (line.vendorId) add(line.vendorId, line.lineId, 'lineVendor');
    for (const supplier of facts.suppliers) if (supplier.itemId === line.itemId) add(supplier.companyId, line.lineId, 'itemSupplier');
    if (line.categoryId) {
      // Repping a category reps its descendants: the line's category and every ancestor of it count.
      const chain = new Set([line.categoryId, ...ancestors(line.categoryId)]);
      for (const rep of activeReps) if (chain.has(rep.itemCategoryId)) add(rep.vendorCompanyId, line.lineId, 'vendorRep');
    }
    for (const prior of facts.priorPurchases) if (prior.itemId === line.itemId) add(prior.vendorCompanyId, line.lineId, 'priorPurchaseOrder');
  }
  return union;
}

/** The lines no vendor covers: shown in their own section, never dropped. */
export function unassignedLineIds(lines: readonly UnionLine[], union: ReadonlyMap<string, VendorSuggestion>): string[] {
  const covered = new Set<string>();
  for (const entry of union.values()) for (const id of entry.lineIds) covered.add(id);
  return lines.filter((l) => !covered.has(l.lineId)).map((l) => l.lineId);
}

/** 🔨 An RFQ cannot be issued while the unassigned section is non-empty without acknowledgement. */
export function issueProblem(unassignedCount: number, acknowledged: boolean): string | null {
  if (unassignedCount > 0 && !acknowledged) return `${counted(unassignedCount, 'line')} ${unassignedCount === 1 ? 'matches' : 'match'} no vendor; acknowledge the unassigned lines to issue the RFQ anyway`;
  return null;
}

/**
 * A prior request on the same Deal that already asked this vendor about lines
 * the new one repeats. The service reads the rows; the two rules below turn
 * them into the words the refusal and the modal both say (decision 0499).
 */
export interface RfqPriorAsk {
  rfqNumber: string;
  /** The calendar date that vendor was asked, in the Organization's zone; null when the row carries no send time. */
  sentOn: string | null;
  /** How many of the lines now ticked that earlier request already carried. */
  overlapCount: number;
}

/** One prior ask in the words the refusal names it by, so the modal and the server say the same thing. */
export function priorAskLine(ask: RfqPriorAsk): string {
  // "1 of these lines" and "3 of these lines" both take the plural, because the set being counted out of is the plural one.
  const lines = `${ask.overlapCount} of these lines`;
  return ask.sentOn === null ? `${ask.rfqNumber} already asked this vendor about ${lines}` : `${ask.rfqNumber} asked this vendor about ${lines} on ${ask.sentOn}`;
}

/**
 * 🔨 An RFQ repeating lines this vendor was already asked about on this Deal
 * is refused until the person confirms the repeat. Re-asking is legitimate —
 * a chase, a revised specification, a stale price — so this warns and never
 * blocks. The caller orders the asks newest first.
 */
export function repeatProblem(priorAsks: readonly RfqPriorAsk[], confirmed: boolean): string | null {
  const first = priorAsks[0];
  if (confirmed || first === undefined) return null;
  const rest = priorAsks.length - 1;
  const earlier = rest > 0 ? `, and ${counted(rest, 'earlier request')} did too` : '';
  return `${priorAskLine(first)}${earlier}; confirm the repeat to ask again`;
}

/** 🔨 One email per vendor contains only their own lines; the other vendors asked are never named. */
export function linesForVendor<T extends { lineId: string }>(lines: readonly T[], vendorLineIds: readonly string[]): T[] {
  const wanted = new Set(vendorLineIds);
  return lines.filter((l) => wanted.has(l.lineId));
}

// ---- The chase and the alarm ----

export const RFQ_RESPONSE_SUBJECT = 'RFQVendorResponse';

/** The chase as a ReminderRule row: interval mode, the period read off the RFQ, one send per vendor row still at Sent. */
export const RFQ_CHASE_RULE = {
  subjectType: RFQ_RESPONSE_SUBJECT,
  anchorField: null,
  offsets: [] as number[],
  intervalDays: null,
  intervalSourceField: 'rfqId.chaseEveryWorkingDays',
  audience: 'Vendor' as const,
  channel: 'Email' as const,
  maxSends: 5,
  stopCondition: 'SubjectClosed' as const,
  businessDaysOnly: true,
} as const;

/** The template the chase sends; the engine picks it by documentType. */
export const RFQ_CHASE_TEMPLATE = {
  name: 'RFQ chase',
  documentType: RFQ_RESPONSE_SUBJECT,
  subject: '[{{rfqNumber}}] A reminder: your quotation for {{organizationName}}',
  bodyRichText: '<p>Hello,</p><p>{{organizationName}} sent you request for quotation {{rfqNumber}} on {{sentOn}} and has not yet received your prices.{{dueLine}}</p><p>Please return the sheet with the RFQ number in the subject, or reply to say you will not be quoting.</p><p>{{requestedBy}}<br>{{organizationName}}</p>',
} as const;

/** The ChecklistItem kind the requester's alarm is raised under. */
export const RFQ_NO_RESPONSE_KIND = 'RfqNoResponse';

/** A vendor that has responded or declined is not chased again; only a row still at Sent is a target. */
export function chaseTargets<T extends { id: string; status: string }>(responses: readonly T[]): T[] {
  return responses.filter((r) => r.status === 'Sent');
}

/** The subject closes for the engine's stop condition once the vendor answered, declined or the quote expired. */
export function responseSubjectClosed(status: string): boolean {
  return status === 'Received' || status === 'Declined' || status === 'Expired';
}

/** Blank on the RFQ reads the Settings default; a period is never zero. */
export function chasePeriod(rfqDays: number | null | undefined, defaultDays: number): number {
  return typeof rfqDays === 'number' && rfqDays >= 1 ? rfqDays : Math.max(1, defaultDays);
}

export function noResponseAlarmDueAt(issuedAt: Date, alarmWorkingDays: number, isHoliday?: (d: Date) => boolean): Date {
  return addBusinessDays(issuedAt, Math.max(1, alarmWorkingDays), isHoliday);
}

export interface AlarmRfqFacts {
  status: string;
  issuedAt: Date | null;
  noResponseAlarmWorkingDays: number | null;
}

/** The alarm fires once nobody has answered by the working-day mark; a vendor's answer or refusal, or the RFQ leaving Issued, silences it. */
export function noResponseAlarmDue(rfq: AlarmRfqFacts, responses: readonly { status: string }[], defaultDays: number, now: Date, isHoliday?: (d: Date) => boolean): boolean {
  if (rfq.status !== 'Issued' || rfq.issuedAt === null) return false;
  if (responses.some((r) => r.status === 'Received' || r.status === 'Declined')) return false;
  const days = rfq.noResponseAlarmWorkingDays ?? defaultDays;
  return noResponseAlarmDueAt(rfq.issuedAt, days, isHoliday).getTime() <= now.getTime();
}

export function noResponseItemTitle(rfqNumber: string, workingDays: number): string {
  return `${rfqNumber}: no vendor has answered after ${counted(workingDays, 'working day')}`;
}

// ---- Responses and the comparison ----

/** A vendor pricing eight of ten lines is a partial response, the normal case. */
export function responseCompleteness(askedLineCount: number, pricedLineCount: number): 'None' | 'Partial' | 'Full' {
  if (pricedLineCount <= 0) return 'None';
  return pricedLineCount >= askedLineCount ? 'Full' : 'Partial';
}

/** The RFQ reads ResponsesIn once any vendor has answered, and Issued until then; Awarded and Cancelled are a person's writes. */
export function rfqStatusAfterResponses(current: string, responses: readonly { status: string }[]): string {
  if (current !== 'Issued' && current !== 'ResponsesIn') return current;
  return responses.some((r) => r.status === 'Received') ? 'ResponsesIn' : 'Issued';
}

export interface ComparisonLine {
  lineId: string;
  lineNumber: number;
  sku: string | null;
  description: string;
  quantity: number;
  unitName: string;
  /** The BOM line's current price, in cents, for the comparison to read beside the quotes. */
  bomPriceCents: number | null;
}

export interface ComparisonResponseLine {
  rfqLineId: string;
  unitPriceCents: number;
  leadTimeDays: number | null;
  minimumQuantity: number | null;
  notes: string | null;
}

export interface ComparisonResponse {
  responseId: string;
  vendorCompanyId: string;
  vendorName: string;
  /** 🔒 The vendor's billing address as mailing lines, which the Details view's parties block draws per vendor asked (T442; decision 1120); absent from an older reply. */
  vendorLines?: string[];
  status: string;
  sentAt: string | null;
  respondedAt: string | null;
  vendorQuoteNumber: string | null;
  expiresAt: string | null;
  notes: string | null;
  lines: ComparisonResponseLine[];
}

export interface ComparisonCell extends ComparisonResponseLine {
  /** Marked on the lowest priced cell of the row; a tie marks each. */
  lowest: boolean;
  extendedCents: number;
}

export interface ComparisonRow {
  line: ComparisonLine;
  cells: Record<string, ComparisonCell | null>;
}

export interface ComparisonTotal {
  responseId: string;
  pricedLines: number;
  completeness: 'None' | 'Partial' | 'Full';
  /** The sum over the lines this vendor priced; vendors pricing different lines are not compared on it alone. */
  totalCents: number;
}

export interface ComparisonMatrix {
  rows: ComparisonRow[];
  totals: ComparisonTotal[];
}

const roundCents = (value: number): number => roundHalfUpCents(value);

/** Every line against every vendor asked, the lowest price on each row marked, and each vendor's completeness and total. */
export function comparisonMatrix(lines: readonly ComparisonLine[], responses: readonly ComparisonResponse[]): ComparisonMatrix {
  const rows: ComparisonRow[] = lines.map((line) => {
    const cells: Record<string, ComparisonCell | null> = {};
    let lowest: number | null = null;
    for (const response of responses) {
      const found = response.lines.find((l) => l.rfqLineId === line.lineId) ?? null;
      cells[response.responseId] = found ? { ...found, lowest: false, extendedCents: roundCents(found.unitPriceCents * line.quantity) } : null;
      if (found && (lowest === null || found.unitPriceCents < lowest)) lowest = found.unitPriceCents;
    }
    for (const cell of Object.values(cells)) if (cell && lowest !== null && cell.unitPriceCents === lowest) cell.lowest = true;
    return { line, cells };
  });
  const totals: ComparisonTotal[] = responses.map((response) => {
    const priced = response.lines.filter((l) => lines.some((line) => line.lineId === l.rfqLineId));
    return {
      responseId: response.responseId,
      pricedLines: priced.length,
      completeness: responseCompleteness(lines.length, priced.length),
      totalCents: priced.reduce((sum, l) => sum + roundCents(l.unitPriceCents * (lines.find((line) => line.lineId === l.rfqLineId)?.quantity ?? 0)), 0),
    };
  });
  return { rows, totals };
}

// ---- The shapes the API and the screen exchange ----

export interface RecordResponseLineInput {
  rfqLineId: string;
  unitPriceCents: number;
  leadTimeDays?: number | null;
  minimumQuantity?: number | null;
  notes?: string | null;
}

/** A response recorded by hand on the comparison screen, until Vendor Response Matching lands; `declined` records a refusal with no lines. */
export interface RecordResponseRequest {
  responseId: string;
  declined?: boolean;
  vendorQuoteNumber?: string | null;
  /** A calendar date, YYYY-MM-DD. */
  expiresOn?: string | null;
  notes?: string | null;
  receivedFromMessageId?: string | null;
  lines?: RecordResponseLineInput[];
}

export interface AddRfqVendorRequest {
  vendorCompanyId: string;
  contactEmploymentId?: string | null;
  email?: string | null;
  message?: string | null;
}

/**
 * 🔒 What Edit on the RFQ's page reopens (decision 1054): the three things set at
 * issue, each blank reading its Settings default as it did then.
 */
export interface EditRfqRequest {
  /** A calendar date, YYYY-MM-DD, read in the Organization's time zone at five in the afternoon. */
  responsesDueOn: string | null;
  chaseEveryWorkingDays: number | null;
  noResponseAlarmWorkingDays: number | null;
}

export interface UseForBomPricingRequest {
  responseId: string;
  /** The RFQ lines to write back; every priced line when absent. */
  rfqLineIds?: string[];
  /** The person's answer to the costed-change confirmation, carried on the press that follows the ask (decision 1466; T3391). */
  costChange?: BomCostChangeReply;
}

export interface AwardRfqRequest {
  responseId: string;
  /** WEA's receiving site; the drop-ship case takes it from the Sales Order instead. */
  shipToSiteId?: string | null;
  fulfillmentMethod?: 'Stock' | 'DropShip';
  salesOrderId?: string | null;
}

export interface RfqComparisonAnswer {
  rfqId: string;
  rfqNumber: string;
  status: string;
  dealId: string | null;
  dealName: string | null;
  issuedAt: string | null;
  responsesDueAt: string | null;
  /** The due day in the Organization's time zone, which the Edit form opens on (decision 1054); absent from an older reply. */
  responsesDueOn?: string | null;
  chaseEveryWorkingDays: number | null;
  noResponseAlarmWorkingDays: number | null;
  awardedResponseId: string | null;
  awardedPurchaseOrder: { id: string; purchaseOrderNumber: string } | null;
  lines: ComparisonLine[];
  responses: ComparisonResponse[];
  matrix: ComparisonMatrix;
  /** WEA's own sites, for the award's Ship To. */
  receivingSites: { id: string; label: string }[];
  /**
   * 🔒 The one projection the page's PDF view draws, carried by this answer the
   * way every other family carries its own (decision 0613; piece 27).
   * 💰 It arrived on a route of its own for one day, and a page reading two
   * answers drew the view it had when the first landed.
   */
  printDocument?: PrintDocument | null;
  /**
   * 🔒 The layout the request prints on, resolved beside the projection
   * (decision 0567), so the PDF view positions the regions the paper positions.
   * Absent where nothing is laid out, and the view falls back to reading order.
   */
  printLayout?: PrintSheetLayout | null;
  /** 🔒 The sheet both views read, as the printer's own HTML, in this same answer (T169). */
  printSheet?: PrintSheetHtml | null;
  /** 🔒 Why this viewer writes nothing on the document, or null where they may (decision 0687). */
  writeRefusal?: string | null;
}
